import { LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { SELECTORS } from '../../constants/selectors';

/**
 * 注文詳細アコーディオンコンポーネント
 *
 * @element kd-order-accordion
 * @description 注文詳細の開閉アニメーション（Web Animations API）を制御する。
 *              既存の `.details` / `.details__summary` / `.details__content` 構造をそのまま使用。
 *
 * @attr {number} duration - アニメーション時間（ms）。デフォルト: 200
 */
@customElement('kd-order-accordion')
export class KdOrderAccordion extends LitElement {
  createRenderRoot() {
    return this;
  }

  @property({ type: Number })
  duration = 200;

  private boundHandleSummaryClick: ((e: Event) => void) | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    this.boundHandleSummaryClick = this.handleSummaryClick.bind(this);

    const summaries = this.querySelectorAll(SELECTORS.ACCOUNT.ACCORDION_SUMMARY);
    summaries.forEach((summary) => {
      summary.addEventListener('click', this.boundHandleSummaryClick!);
    });
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();

    if (this.boundHandleSummaryClick) {
      const summaries = this.querySelectorAll(SELECTORS.ACCOUNT.ACCORDION_SUMMARY);
      summaries.forEach((summary) => {
        summary.removeEventListener('click', this.boundHandleSummaryClick!);
      });
      this.boundHandleSummaryClick = null;
    }
  }

  private handleSummaryClick(e: Event): void {
    e.preventDefault();

    const summary = (e.currentTarget as HTMLElement);
    const details = summary.closest(SELECTORS.ACCOUNT.ACCORDION_ITEM);
    if (!details) return;

    const content = details.querySelector(SELECTORS.ACCOUNT.ACCORDION_CONTENT) as HTMLElement | null;
    const toggle = details.querySelector(SELECTORS.ACCOUNT.ACCORDION_TOGGLE);
    if (!content || !toggle) return;

    const isOpen = (details as HTMLElement).dataset.open === 'true';

    if (isOpen) {
      this.closeAccordion(details as HTMLElement, content, toggle);
    } else {
      this.openAccordion(details as HTMLElement, content, toggle);
    }
  }

  private closeAccordion(details: HTMLElement, content: HTMLElement, toggle: Element): void {
    const animation = content.animate(
      { opacity: [1, 0], height: [content.offsetHeight + 'px', '0px'] },
      { duration: this.duration, easing: 'ease-out' },
    );

    animation.onfinish = () => {
      details.dataset.open = 'false';
      // v2 の Tailwind は prefix(tw) 設定のため素の 'hidden' では display:none が効かない。
      content.classList.add('tw:hidden');
      toggle.textContent = '+';
    };
  }

  private openAccordion(details: HTMLElement, content: HTMLElement, toggle: Element): void {
    details.dataset.open = 'true';
    content.classList.remove('tw:hidden');
    toggle.textContent = '\u2212';

    content.animate(
      { opacity: [0, 1], height: ['0px', content.offsetHeight + 'px'] },
      { duration: this.duration, easing: 'ease-out' },
    );
  }

  render() {
    return nothing;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kd-order-accordion': KdOrderAccordion;
  }
}
